import styled from '@emotion/styled';
import {
  Alert,
  AlertTitle,
  Autocomplete,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { useState } from 'react';
import { useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { authenticatedFetch } from '../../authentication/authenticatedFetch';
import { useProject } from '../../project/context/useProject';
import { downloadFile } from '../../project/ProjectsUtils';
import { TitleBlock } from '../../ui/Block';
import Button from '../../ui/Button';
import { PageContainer } from '../../ui/Container';
import { ReactComponent as SkipIcon } from '../../ui/icons/skip.svg';
import LoadingButton from '../../ui/LoadingButton';
import ActionCard from './ActionCard';
import { useTranslation } from 'react-i18next';
import { usePostHog } from 'posthog-js/react';
import {
  getPageName,
  getServiceTypeFromProject,
} from '../../analytics/posthogEvents';

export const Options = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  width: 100%;
  margin-top: 16px;
`;

const ActionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(380px, 1fr));
  gap: 16px;
  width: 100%;
  align-items: stretch;
`;

const FiltersContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 16px;
  width: 100%;
`;

const TopFiltersRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`;

const CountContainer = styled.div`
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: 0em;
  text-align: center;

  span {
    font-size: 16px;
    font-weight: 400;
  }
`;

const SortContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  font-size: 16px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0em;
  align-items: center;
`;

const ActionsPage = () => {
  const { projectId } = useParams();
  const { project } = useProject(projectId);
  const [sortOrder, setSortOrder] = useState<
    'impact' | 'duration' | 'cost' | 'humanResources'
  >('impact');
  const [selectedSectors, setSelectedSectors] = useState<string[]>([]);
  const [downloadLoading, setDownloadLoading] = useState<boolean>(false);
  const { t } = useTranslation(['project']);
  const posthog = usePostHog();
  const location = useLocation();

  useEffect(() => {
    const fromPage =
      (location.state as { from_page?: string } | null)?.from_page || 'project';
    posthog?.capture('actions viewed', {
      step_name: 'actions_viewed',
      step_order: 3,
      model_type: getServiceTypeFromProject(project),
      from_page: fromPage,
      to_page: 'actions',
    });
  }, [location.state, posthog, project]);

  useEffect(() => {
    posthog?.capture('actions filtered', {
      button_name: 'actions_filter',
      page_name: 'actions',
      action_name: 'filter_or_sort',
      selected_sectors: selectedSectors.join(','),
      sort_order: sortOrder,
    });
  }, [posthog, selectedSectors, sortOrder]);

  const navigate = useNavigate();
  if (!project) {
    return null;
  }

  const uniqueSectors = Array.from(
    new Set(project.actions.map((a) => a.sector)),
  );

  const filteredActions = project.actions
    .filter(
      (action) =>
        selectedSectors.length === 0 || selectedSectors.includes(action.sector),
    )
    .sort((a, b) => {
      if (sortOrder === 'impact') {
        return b.impactValue - a.impactValue;
      } else if (
        sortOrder === 'duration' ||
        sortOrder === 'cost' ||
        sortOrder === 'humanResources'
      ) {
        return a[sortOrder] - b[sortOrder];
      }
      return 0;
    });

  const handleDownloadActions = async () => {
    if (!projectId) return;

    try {
      setDownloadLoading(true);

      const params = new URLSearchParams();
      if (selectedSectors.length) {
        params.set('sectors', selectedSectors.join(','));
      }
      if (sortOrder) {
        params.set('sortOrder', sortOrder);
      }

      const response = await authenticatedFetch(
        `${process.env.REACT_APP_API_URL
        }/projects/${projectId}/actions?${params.toString()}`,
        {
          method: 'GET',
          headers: new Headers({
            Accept: 'application/vnd.ms-excel',
            'Content-Type': 'application/json',
          }),
        },
      );

      const actionsFileBlob = await response.blob();
      const safeName =
        (project.name || 'project')
          .toString()
          .replace(/[/\\?%*:|"<>]/g, '_')
          .substring(0, 100) || 'project';

      const filename = `${safeName}-actions.xlsx`;

      downloadFile(actionsFileBlob, filename);
      posthog?.capture('action_plan downloaded', {
        document_type: 'plan_action',
        page_name: 'actions',
        model_type: getServiceTypeFromProject(project),
      });
    } finally {
      setDownloadLoading(false);
    }
  };

  return (
    <PageContainer bgColor="var(--backgroundColor)">
      <TitleBlock background="var(--actions)">
        <h3>{project.name} - Mes actions</h3>
        <p>
          Découvrez les actions à mettre en oeuvre pour un projet responsable
        </p>
        <Options>
          <Button
            onClick={() => {
              posthog?.capture('results viewed', {
                step_name: 'results_viewed',
                step_order: 2,
                model_type: getServiceTypeFromProject(project),
                from_page: 'actions',
                to_page: 'results',
              });
              navigate(`/project/${projectId}/results`, {
                state: { from_page: getPageName(location.pathname) },
              });
            }}
            startIcon={<SkipIcon />}
          >
            Voir les résultats complets
          </Button>
          <LoadingButton
            onClick={handleDownloadActions}
            loading={downloadLoading}
            color="primary"
            disabled={downloadLoading}
          >
            Télécharger (.xlsx)
          </LoadingButton>
        </Options>
      </TitleBlock>
      <Alert severity="warning" variant="outlined" sx={{ mb: 2, color: "black" }}>
        <AlertTitle>{t('project:actions-page.disclaimer.title')}</AlertTitle>
        {t('project:actions-page.disclaimer.content')}
      </Alert>
      <FiltersContainer>
        <TopFiltersRow>
          <CountContainer>
            {filteredActions.length}
            <span> Action(s)</span>
          </CountContainer>
          <SortContainer>
            <Autocomplete
              multiple
              options={uniqueSectors}
              value={selectedSectors}
              onChange={(_, value) => setSelectedSectors(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Catégories"
                  size="small"
                  sx={{ minWidth: '200px' }}
                />
              )}
            />
            <FormControl size="small">
              <InputLabel id="sort-select-label">Tri</InputLabel>
              <Select
                labelId="sort-select-label"
                id="sort-select"
                value={sortOrder}
                label="Critère"
                onChange={(e) =>
                  setSortOrder(
                    e.target.value as
                    | 'impact'
                    | 'duration'
                    | 'cost'
                    | 'humanResources',
                  )
                }
              >
                <MenuItem value="impact">Impact</MenuItem>
                <MenuItem value="duration">
                  Durée d'implémentation estimée
                </MenuItem>
                <MenuItem value="cost">Niveau de coût estimé</MenuItem>
                <MenuItem value="humanResources">
                  Ressources humaines nécessaires
                </MenuItem>
              </Select>
            </FormControl>
          </SortContainer>
        </TopFiltersRow>
      </FiltersContainer>
      <ActionsGrid>
        {filteredActions.map((action, index) => (
          <ActionCard action={action} key={index} />
        ))}
      </ActionsGrid>
    </PageContainer>
  );
};

export default ActionsPage;
